import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { TrendingUp } from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtWeek = (iso) => {
  try { return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }); }
  catch { return iso; }
};

// Week-by-week Spoke To + Wins momentum chart for the Sales Funnel tab.
export const FunnelTrendCard = ({ domainId, site }) => {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    if (site) p.set('site', site);
    axios.get(`${API}/analytics/sales-funnel-trend?${p}`, { timeout: 30000 })
      .then(r => setSeries(r.data?.series || []))
      .catch(() => setSeries([]))
      .finally(() => setLoading(false));
  }, [domainId, site]);

  const hasData = series.some(w => w.call_attempts || w.wins);

  return (
    <Card data-testid="funnel-trend-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="w-4 h-4" /> Weekly Momentum {site ? `— ${site}` : '— all my sites'}
          <span className="text-xs font-normal text-slate-400">Spoke To vs Wins, last 12 weeks</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-48 text-slate-400 text-sm">Loading…</div>
        ) : !hasData ? (
          <div className="flex items-center justify-center h-24 text-slate-400 text-sm" data-testid="funnel-trend-empty">
            No outbound activity in the last 12 weeks.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={series} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="week" tickFormatter={fmtWeek} tick={{ fontSize: 10, fill: '#94a3b8' }} />
              <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#f59e0b' }} allowDecimals={false} />
              <Tooltip
                labelFormatter={(v) => `Week of ${fmtWeek(v)}`}
                formatter={(value, name) => [value, name === 'spoke_to' ? 'Spoke To' : name === 'wins' ? 'Wins' : 'Call Attempts']}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
              />
              <Legend formatter={(v) => (v === 'spoke_to' ? 'Spoke To' : v === 'wins' ? 'Wins' : v)} wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="left" dataKey="spoke_to" fill="#0d9488" radius={[3, 3, 0, 0]} maxBarSize={28} />
              <Line yAxisId="right" type="monotone" dataKey="wins" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default FunnelTrendCard;
